/**
 * Where the prompt box sits when a screen gives it a place of its own: Agentes › Conversas
 * (docs/refactor/05-navegacao.md). The box is one component for the whole app (it keeps the
 * conversations, the text being written and the live answer across screens), so instead of being
 * mounted twice it lays itself over the place the screen marks — the "slot" — for as long as it is
 * there. Elsewhere it is docked along the bottom, floating or closed, as always.
 */

import { useEffect, useState, useSyncExternalStore } from 'react'

let slot: HTMLElement | null = null
const listeners = new Set<() => void>()

/** The screen's place for the box; null when the screen goes away. */
export function setDockSlot(element: HTMLElement | null): void {
  if (slot === element) return
  slot = element
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The slot, while a screen offers one. */
export function useDockSlot(): HTMLElement | null {
  return useSyncExternalStore(subscribe, () => slot, () => null)
}

export interface SlotRect {
  left: number
  top: number
  width: number
  height: number
}

/** The slot's place on the screen, kept up to date as the window, the sidebar or the page moves. */
export function useSlotRect(element: HTMLElement | null): SlotRect | null {
  const [rect, setRect] = useState<SlotRect | null>(null)
  useEffect(() => {
    if (!element) {
      setRect(null)
      return
    }
    const measure = () => {
      const r = element.getBoundingClientRect()
      setRect((current) =>
        current && current.left === r.left && current.top === r.top && current.width === r.width && current.height === r.height
          ? current
          : { left: r.left, top: r.top, width: r.width, height: r.height },
      )
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(element)
    observer?.observe(document.documentElement)
    window.addEventListener('resize', measure)
    // The page scrolls inside <main>: capture catches it there.
    window.addEventListener('scroll', measure, true)
    // The sidebar folding (Ctrl+B) moves the slot without resizing the window.
    const timer = window.setInterval(measure, 500)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      window.clearInterval(timer)
    }
  }, [element])
  return rect
}
